import React, { useState, useEffect } from "react";
import { Table, Input, Button, Card, Tooltip, Spin } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { tiposNovedadesApi } from "../../services/api";
import TipoNovedadFormModal from "./TipoNovedadFormModal";
import DeleteConfirmModal from "../DeleteConfirmModal";

const TablaTiposNovedades = () => {
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTipoNovedad, setEditingTipoNovedad] = useState(null);
  const [tiposNovedades, setTiposNovedades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [tipoNovedadToDelete, setTipoNovedadToDelete] = useState(null);

  // Cargar datos al iniciar el componente
  useEffect(() => {
    fetchTiposNovedades()
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error cargando datos iniciales:", error);
        toast.error("Error al cargar los datos", {
          position: "top-right",
          autoClose: 4000,
        });
        setLoading(false);
      });
  }, []);

  // Función para obtener tipos de novedades
  const fetchTiposNovedades = async () => {
    try {
      const response = await tiposNovedadesApi.getAll();
      if (response.data.status) {
        const tiposNovedadesData = response.data.tipos_novedades.map((item) => ({
          key: item.id.toString(),
          id: item.id,
          novedad: item.novedad,
          descripcion: item.descripcion,
          created_at: item.created_at,
          updated_at: item.updated_at,
        }));
        setTiposNovedades(tiposNovedadesData);
      }
    } catch (error) {
      console.error("Error obteniendo tipos de novedades:", error);
      toast.error("Error al cargar los tipos de novedades", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          size="small"
          style={{ width: 90, marginRight: 8 }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Buscar
        </Button>
        <Button
          onClick={() => clearFilters()}
          size="small"
          style={{ width: 90 }}
        >
          Limpiar
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const columns = [
    {
      title: <div className="font-medium">ID</div>,
      dataIndex: "id",
      key: "id",
      width: "10%",
    },
    {
      title: <div className="font-medium">Novedad</div>,
      dataIndex: "novedad",
      key: "novedad",
      ...getColumnSearchProps("novedad"),
      width: "25%",
    },
    {
      title: <div className="font-medium">Descripción</div>,
      dataIndex: "descripcion",
      key: "descripcion",
      ...getColumnSearchProps("descripcion"),
      width: "45%",
    },
    {
      title: <div className="font-medium text-center">Acciones</div>,
      key: "acciones",
      align: "center",
      render: (_, record) => (
        <div className="flex gap-2 justify-center">
          <Tooltip title="Editar tipo de novedad">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              type="primary"
              size="middle"
              className="bg-blue-500 hover:bg-blue-700"
            />
          </Tooltip>
          <Tooltip title="Eliminar tipo de novedad">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              type="primary"
              danger
              size="middle"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleReset = () => {
    setSearchText("");
    fetchTiposNovedades();
  };

  const showModal = () => {
    setIsModalVisible(true);
    setEditingTipoNovedad(null);
  };

  const handleEdit = (tipoNovedad) => {
    setEditingTipoNovedad(tipoNovedad);
    setIsModalVisible(true);
  };

  const handleDelete = (tipoNovedad) => {
    setTipoNovedadToDelete(tipoNovedad);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    try {
      await tiposNovedadesApi.delete(tipoNovedadToDelete.id);
      toast.success(`Tipo de novedad "${tipoNovedadToDelete.novedad}" eliminado correctamente`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setDeleteModalVisible(false);
      setTipoNovedadToDelete(null);
      fetchTiposNovedades(); // Recargar la lista de tipos de novedades
    } catch (error) {
      console.error("Error eliminando tipo de novedad:", error);
      let errorMsg = "Error al eliminar el tipo de novedad";
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMsg = error.response.data.message;
      }
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    setEditingTipoNovedad(null);
    fetchTiposNovedades(); // Recargar la lista después de un cambio exitoso
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-[calc(100vh-100px)]">
        <Spin size="large" tip="Cargando tipos de novedades..." />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card
        title={
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold text-gray-800">
              Administración de Tipos de Novedades
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showModal}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Nuevo Tipo de Novedad
            </Button>
          </div>
        }
        className="shadow-md"
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <Input
              placeholder="Buscar tipo de novedad..."
              className="w-72"
              prefix={<SearchOutlined className="text-gray-400" />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="large"
            />
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            type="default"
            className="h-10"
          >
            Reiniciar filtros
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={tiposNovedades.filter(
            (item) =>
              item.novedad.toLowerCase().includes(searchText.toLowerCase()) ||
              item.descripcion.toLowerCase().includes(searchText.toLowerCase())
          )}
          rowClassName={() =>
            "hover:bg-blue-50 transition-colors cursor-pointer"
          }
          scroll={{ y: "calc(100vh - 350px)" }}
          pagination={{
            total: tiposNovedades.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} tipos de novedad`,
          }}
          className="border border-gray-200 rounded-md"
        />

        {/* Modal para crear/editar tipo de novedad */}
        <TipoNovedadFormModal
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onSuccess={handleModalSuccess}
          editingTipoNovedad={editingTipoNovedad}
        />

        {/* Modal de confirmación para eliminar tipo de novedad */}
        <DeleteConfirmModal
          visible={deleteModalVisible}
          onCancel={() => setDeleteModalVisible(false)}
          onConfirm={confirmDelete}
          title="Eliminar Tipo de Novedad"
          itemDescription="el tipo de novedad"
          itemName={tipoNovedadToDelete ? tipoNovedadToDelete.novedad : ""}
        />
      </Card>
    </div>
  );
};

export default TablaTiposNovedades;